/* ============================================================================
   Cafe Raymond — Global storefront JS
   - Cart add (AJAX) for Quick Add buttons
   - Cart count update in header
   - Quick-add dialog for multi-variant products
   - Toast notifications for "Added to cart"
   No framework — vanilla. Loaded once in theme.liquid.
============================================================================ */

(function () {
  'use strict';

  // ── Utilities ────────────────────────────────────────────────────────────
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }

  function formatMoney(cents) {
    // Falls back to USD; storefront's actual money_format is rendered server-side
    // anywhere it matters. This is for client-only updates (after add-to-cart).
    return '$' + (cents / 100).toFixed(2);
  }

  // ── Cart count ───────────────────────────────────────────────────────────
  function setCartCount(n) {
    $$('[data-cr-cart-count]').forEach(el => {
      el.textContent = n;
      if (n > 0) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
  }

  async function refreshCartCount() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      setCartCount(cart.item_count || 0);
    } catch (e) { /* silent */ }
  }

  // ── Toast ────────────────────────────────────────────────────────────────
  let toastEl;
  function ensureToast() {
    if (toastEl) return toastEl;
    toastEl = document.createElement('div');
    toastEl.className = 'cr-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    toastEl.innerHTML = '<span class="cr-toast__msg"></span><a href="/cart" class="cr-toast__cta">View cart</a>';
    document.body.appendChild(toastEl);
    return toastEl;
  }
  function showToast(message) {
    const t = ensureToast();
    t.querySelector('.cr-toast__msg').textContent = message;
    t.classList.add('is-visible');
    clearTimeout(t._hideTimer);
    t._hideTimer = setTimeout(() => t.classList.remove('is-visible'), 3500);
  }

  // ── Add to cart (AJAX) ───────────────────────────────────────────────────
  async function addToCart(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity })
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => null);
      throw new Error((errData && errData.description) || 'Could not add item');
    }
    return res.json();
  }

  // ── Quick-add dialog ─────────────────────────────────────────────────────
  let qaDialog, qaBody;

  function ensureDialog() {
    if (qaDialog) return qaDialog;
    qaDialog = document.getElementById('cr-qa-dialog');
    if (!qaDialog) return null;
    qaBody = qaDialog.querySelector('[data-cr-qa-body]');
    return qaDialog;
  }

  function buildDialogBody(product, currentVariantId) {
    const variant = product.variants.find(v => v.id === currentVariantId) || product.variants[0];
    const imageUrl = (variant.featured_image && variant.featured_image.src)
      || product.featured_image
      || (product.images && product.images[0])
      || '';

    let html = '';
    html += '<div class="cr-qa__image-wrap">';
    if (imageUrl) {
      html += '<img src="' + imageUrl + '" alt="' + escapeHtml(product.title) + '">';
    } else {
      html += '<div class="cr-image-placeholder">No image</div>';
    }
    html += '</div>';

    html += '<div class="cr-qa__buy">';
    html += '<h2 class="cr-qa__title" id="cr-qa-title">' + escapeHtml(product.title) + '</h2>';
    html += '<p class="cr-qa__price" data-cr-qa-price>' + formatMoney(variant.price) + '</p>';

    // Option pills (only for options that actually vary)
    if (product.options && product.variants.length > 1) {
      product.options.forEach((optName, idx) => {
        const values = Array.from(new Set(product.variants.map(v => v.options[idx])));
        if (values.length <= 1) return;
        html += '<div class="cr-qa__option">';
        html += '<span class="cr-qa__option-label">' + escapeHtml(optName) + '</span>';
        html += '<div class="cr-qa__option-pills" data-cr-qa-option-group="' + idx + '">';
        values.forEach(val => {
          const isSelected = variant.options[idx] === val;
          html += '<button type="button" class="cr-qa__pill' + (isSelected ? ' is-active' : '') + '"';
          html += ' data-cr-qa-option-value="' + escapeHtml(val) + '" data-cr-qa-option-index="' + idx + '">';
          html += escapeHtml(val) + '</button>';
        });
        html += '</div></div>';
      });
    }

    html += '<div class="cr-qa__actions">';
    html += '<button type="button" class="cr-btn-primary cr-btn-primary--full" data-cr-qa-add';
    html += variant.available ? '' : ' disabled';
    html += '>';
    html += variant.available ? 'Add to cart' : 'Sold out';
    html += '</button>';
    html += '<a href="' + (product.url || '#') + '" class="cr-qa__view-details">View full details →</a>';
    html += '</div>';
    html += '</div>';

    return html;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  function openQuickAdd(product) {
    const dialog = ensureDialog();
    if (!dialog) {
      // Fallback — no dialog snippet on page, just navigate to PDP
      window.location.href = product.url;
      return;
    }
    const initialVariant = product.variants.find(v => v.available) || product.variants[0];
    let selectedVariantId = initialVariant.id;
    const currentSelection = initialVariant.options.slice();

    qaBody.innerHTML = buildDialogBody(product, selectedVariantId);

    // Wire pill clicks
    qaBody.querySelectorAll('[data-cr-qa-option-value]').forEach(pill => {
      pill.addEventListener('click', () => {
        const idx = parseInt(pill.dataset.crQaOptionIndex, 10);
        const val = pill.dataset.crQaOptionValue;
        currentSelection[idx] = val;

        // Toggle active state within group
        const group = pill.parentElement;
        group.querySelectorAll('.cr-qa__pill').forEach(p => p.classList.remove('is-active'));
        pill.classList.add('is-active');

        // Find matching variant
        const match = product.variants.find(v =>
          v.options.every((o, i) => o === currentSelection[i])
        );
        if (match) {
          selectedVariantId = match.id;
          const priceEl = qaBody.querySelector('[data-cr-qa-price]');
          if (priceEl) priceEl.textContent = formatMoney(match.price);
          const addBtn = qaBody.querySelector('[data-cr-qa-add]');
          if (addBtn) {
            if (match.available) {
              addBtn.disabled = false;
              addBtn.textContent = 'Add to cart';
            } else {
              addBtn.disabled = true;
              addBtn.textContent = 'Sold out';
            }
          }
        }
      });
    });

    // Wire add button
    const addBtn = qaBody.querySelector('[data-cr-qa-add]');
    if (addBtn) {
      addBtn.addEventListener('click', async () => {
        addBtn.disabled = true;
        const originalText = addBtn.textContent;
        addBtn.textContent = 'Adding…';
        try {
          await addToCart(selectedVariantId, 1);
          await refreshCartCount();
          dialog.close();
          showToast('Added to cart');
        } catch (err) {
          addBtn.disabled = false;
          addBtn.textContent = originalText;
          showToast(err.message || 'Could not add item');
        }
      });
    }

    dialog.showModal();
  }

  // ── Wire global Quick-Add buttons ────────────────────────────────────────
  function initQuickAddButtons() {
    document.body.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-cr-quick-add]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      const productUrl = btn.dataset.crProductUrl;
      const variantId = btn.dataset.crVariantId;
      const hasMultipleVariants = btn.dataset.crMultiVariant === 'true';

      // Single variant — fire AJAX directly
      if (!hasMultipleVariants && variantId) {
        const originalLabel = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        try {
          await addToCart(parseInt(variantId, 10), 1);
          await refreshCartCount();
          showToast('Added to cart');
        } catch (err) {
          showToast(err.message || 'Could not add item');
        } finally {
          btn.textContent = originalLabel;
          btn.disabled = false;
        }
        return;
      }

      // Multi-variant — fetch product JSON and open dialog
      if (productUrl) {
        try {
          btn.disabled = true;
          const res = await fetch(productUrl + '.js', { headers: { Accept: 'application/json' } });
          if (!res.ok) throw new Error('Could not load product');
          const product = await res.json();
          openQuickAdd(product);
        } catch (err) {
          showToast(err.message || 'Could not load product');
        } finally {
          btn.disabled = false;
        }
      }
    });
  }

  // ── Boot ─────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuickAddButtons);
  } else {
    initQuickAddButtons();
  }
})();
